import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOrg } from '../data/orgs';
import { useVisibleDeals } from '../data/permissions';
import { isSupersededDoc, docViewOf, docTypeLabel } from '../data/deals';
import { useDocTypeCatalog } from '../data/docTypes';
import { db } from '../firebaseConfig';
import Icon from '../components/Icons';
import { HelpDot } from '../components/GuidePanel';

/* The library — every artifact across every project you can see, in one
   browsable surface: documents, presentations, workbooks, filings,
   correspondence. Projects stay the home of work; this is the cross-project
   way IN (the sidebar's Documents / Presentations / Workbooks links land
   here pre-filtered). Data: one docs fetch per visible deal (same pattern as
   the Projects page's search index) — no collection-group query, so the
   existing per-deal rules keep authorizing reads. */

const FILE_TYPE_STYLE = {
  PPTX: '#C43E1C', DOCX: '#2B579A', PDF: '#B3261E', TXT: '#5F6B72', EML: '#8C6D1F', AI: '#6C4AB6', XLSX: '#1D6F42',
};
function docFileType(d) {
  if (docViewOf(d) === 'deck') return 'PPTX';
  if (docViewOf(d) === 'workbook') return 'XLSX';
  const f = String(d.format || '').toLowerCase();
  if (f === 'xlsx' || f === 'xlsm') return 'XLSX';
  if (f === 'docx' || f === 'doc') return 'DOCX';
  if (f === 'pdf') return 'PDF';
  if (f === 'txt') return 'TXT';
  if (f === 'pasted') return d.kind === 'email' ? 'EML' : 'TXT';
  if (f === 'authored' || f === 'derived') return 'AI';
  return null;
}

/* Artifact kinds — the chips, the sidebar deep links (?kind=), and the
   classifier. One doc lands in exactly one bucket. */
export const LIBRARY_KINDS = [
  { key: 'documents', label: 'Documents', icon: 'doc', hint: 'Agreements, memos, design docs — every working paper' },
  { key: 'presentations', label: 'Presentations', icon: 'deck', hint: 'Slide decks — agent-authored or imported, exportable to PowerPoint' },
  { key: 'workbooks', label: 'Workbooks', icon: 'sheet', hint: 'Excel models — versioned cell-by-cell, agent-editable, auditable' },
  { key: 'filings', label: 'Filings & certificates', icon: 'shield', hint: 'Executed copies, state filings, issued certificates — evidence on record' },
  { key: 'sources', label: 'Correspondence & sources', icon: 'chat', hint: 'Email threads, received originals, exhibits, reference material' },
];

export function libraryKindOf(d) {
  if (docViewOf(d) === 'deck') return 'presentations';
  if (docViewOf(d) === 'workbook' || ['xlsx', 'xlsm'].includes(String(d.format || '').toLowerCase())) return 'workbooks';
  if (d.kind === 'filing') return 'filings';
  if (d.kind && d.kind !== 'primary') return 'sources';
  return 'documents';
}

const compactWhen = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  const days = (Date.now() - ts) / 86400000;
  if (days < 1) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (days < 300) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return d.toLocaleDateString([], { year: 'numeric', month: 'short' });
};

export default function Library() {
  const { org, orgId } = useOrg();
  const { deals } = useVisibleDeals();
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = searchParams.get('kind') || '';
  const type = searchParams.get('type') || '';   // catalog docType key
  const [q, setQ] = useState('');
  const { catalog } = useDocTypeCatalog(orgId);
  // { [dealId]: [docs] } — fetched once per org visit; deals arriving later
  // (permission changes) refetch via the deals dependency.
  const [index, setIndex] = useState(null);

  useEffect(() => { setIndex(null); }, [orgId]);

  useEffect(() => {
    if (index !== null || deals === null) return undefined;
    if (!deals.length) { setIndex({}); return undefined; }
    let alive = true;
    (async () => {
      try {
        const entries = await Promise.all(
          deals.map(async (d) => {
            const snap = await db.collection(`orgs/${orgId}/deals/${d.id}/docs`).get();
            return [d.id, snap.docs.map((x) => ({ id: x.id, ...x.data() }))];
          })
        );
        if (alive) setIndex(Object.fromEntries(entries));
      } catch {
        if (alive) setIndex({});
      }
    })();
    return () => { alive = false; };
  }, [index, deals, orgId]);

  const dealName = useMemo(() => new Map((deals || []).map((d) => [d.id, d.name])), [deals]);

  const rows = useMemo(() => {
    if (!index) return null;
    const out = [];
    for (const [dealId, docs] of Object.entries(index)) {
      for (const d of docs) {
        if (isSupersededDoc(d)) continue; // lineage lives on the project page
        out.push({ ...d, dealId, kindKey: libraryKindOf(d) });
      }
    }
    out.sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
    return out;
  }, [index]);

  const counts = useMemo(() => {
    const c = {};
    for (const r of rows || []) c[r.kindKey] = (c[r.kindKey] || 0) + 1;
    return c;
  }, [rows]);

  // Catalog types present in this org's library, most used first.
  const typeCounts = useMemo(() => {
    const c = new Map();
    for (const r of rows || []) if (r.docType) c.set(r.docType, (c.get(r.docType) || 0) + 1);
    return [...c.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const needle = q.trim().toLowerCase();
  const shown = (rows || []).filter(
    (r) =>
      (!kind || r.kindKey === kind) &&
      (!type || r.docType === type) &&
      (!needle ||
        String(r.title || '').toLowerCase().includes(needle) ||
        String(dealName.get(r.dealId) || '').toLowerCase().includes(needle))
  );

  const setKind = (k) => setSearchParams({ ...(k ? { kind: k } : {}), ...(type ? { type } : {}) }, { replace: true });
  const setType = (t) => setSearchParams({ ...(kind ? { kind } : {}), ...(t ? { type: t } : {}) }, { replace: true });
  const activeKind = LIBRARY_KINDS.find((k) => k.key === kind);

  return (
    <div className="cs-page u-stack m-rise" style={{ '--gap': 'var(--sp-6)' }}>
      <div className="cs-page-head">
        <div>
          <h1>Library</h1>
          <p className="u-muted">
            {org?.name || '…'} — every artifact across your projects: documents, presentations,
            workbooks, filings, and correspondence. <HelpDot topic="library" label="How the library works" />
          </p>
        </div>
      </div>

      <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <input
          className="cs-input"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search artifacts by title or project…"
          aria-label="Search library"
          style={{ maxWidth: 440 }}
        />
        <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)' }}>
          <button
            type="button"
            className="cs-chip"
            style={kind === '' ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
            onClick={() => setKind('')}
          >
            All {rows ? `(${rows.length})` : ''}
          </button>
          {LIBRARY_KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              className="cs-chip"
              style={kind === k.key ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
              data-tip={k.hint}
              onClick={() => setKind(kind === k.key ? '' : k.key)}
            >
              <Icon name={k.icon} size={13} /> {k.label}
              {rows ? ` (${counts[k.key] || 0})` : ''}
            </button>
          ))}
        </div>
        {typeCounts.length > 0 && (
          <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }} data-nova-note="Filter by document type (catalog)">
            <span className="u-xs u-muted">Type</span>
            {typeCounts.slice(0, 14).map(([key, n]) => (
              <button
                key={key}
                type="button"
                className="cs-chip"
                aria-pressed={type === key}
                style={type === key ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
                onClick={() => setType(type === key ? '' : key)}
              >
                {docTypeLabel({ docType: key }, catalog)} ({n})
              </button>
            ))}
            {type && !typeCounts.slice(0, 14).some(([k]) => k === type) && (
              <button type="button" className="cs-chip" aria-pressed style={{ borderColor: 'var(--surf)' }} onClick={() => setType('')}>
                {docTypeLabel({ docType: type }, catalog)} ×
              </button>
            )}
          </div>
        )}
      </div>

      {rows === null && <div className="cs-skeleton" style={{ height: 160 }} />}
      {rows && !rows.length && (
        <div className="cs-empty">
          Nothing in the library yet — artifacts appear here as your projects gain documents,
          presentations, and workbooks. Create a project to get started, or press <kbd style={{ padding: '0 4px' }}>⌘K</kbd> and ask how.
        </div>
      )}
      {rows && rows.length > 0 && !shown.length && (
        <div className="cs-empty">
          No {activeKind ? activeKind.label.toLowerCase() : 'artifacts'} match
          {needle ? ` "${q.trim()}"` : ''} — clear the search or pick another kind.
        </div>
      )}

      <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
        {shown.map((r) => {
          const ft = docFileType(r);
          return (
            <div key={`${r.dealId}:${r.id}`} className="cs-card u-split" style={{ padding: 'var(--sp-3) var(--sp-4)', alignItems: 'center' }}>
              <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center', minWidth: 0 }}>
                {ft && (
                  <span
                    className="u-xs u-tabular"
                    style={{
                      background: FILE_TYPE_STYLE[ft] || '#5F6B72', color: '#fff', fontWeight: 700,
                      padding: '2px 6px', borderRadius: 4, letterSpacing: '0.04em', flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    {ft}
                  </span>
                )}
                <div className="u-stack" style={{ '--gap': '2px', minWidth: 0 }}>
                  <Link to={`/deals/${r.dealId}/docs/${r.id}`} style={{ textDecoration: 'none' }}>
                    <strong style={{ overflowWrap: 'anywhere' }}>{r.title || 'Untitled'}</strong>
                  </Link>
                  <span className="u-xs u-muted">
                    <Link to={`/deals/${r.dealId}`} className="u-muted">{dealName.get(r.dealId) || 'Project'}</Link>
                    {' · '}v{r.versionCount || 0} · updated {compactWhen(r.updatedAt || r.createdAt)}
                    {r.docType ? ` · ${docTypeLabel(r, catalog)}` : r.aiProfile?.docType ? ` · ${r.aiProfile.docType}` : ''}
                  </span>
                </div>
              </div>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
                {(r.tags || []).includes('executed') && <span className="cs-badge b-ok">⚖️ signed</span>}
                <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/deals/${r.dealId}/docs/${r.id}`}>Open</Link>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
